import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@optools/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@optools/components/ui/form';
import { Input } from '@optools/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@optools/components/ui/select';
import { Textarea } from '@optools/components/ui/textarea';
import { useAccessCheck } from '@optools/hooks/useAccessCheck';
import {
  ActionType,
  EsniRule,
  EsniRuleStatus,
  EsniRuleType,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { selectToolsConfig } from '@optools/store/configApiSlice';
import { useAppSelector } from '@optools/store/hooks';
import { formatEnumValue } from '@optools/utils/format';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ActionTypeMultiSelect } from './action-type-multi-select';
import { DeleteEsniRuleDialog } from './delete-esni-rule-dialog';

export interface RuleFormProps {
  defaultFormValues?: EsniRule;
  loading?: boolean;
  onReset: () => void;
  onSubmit: (fields: RuleFormType) => void;
}

const actionTypeInputSchema = z.object({
  actionType: z.enum(ActionType),
  metadata: z.record(z.any(), z.any()),
});

const formSchema = z
  .object({
    actions: z.array(actionTypeInputSchema).min(1, {
      error: 'At least on Action Type is required',
    }),
    name: z.string({ error: 'Rule name required' }).min(1, { error: 'Rule name required' }),
    priority: z.coerce
      .number<number>()
      .int({ error: 'Priority must be an integer' })
      .min(1, { error: 'Priority must be greater than 0' }),
    query: z.string().min(1, { error: 'Rule query required' }),
    status: z.enum(EsniRuleStatus, { error: 'Rule status required' }),
    type: z.enum(EsniRuleType, { error: 'Rule type required' }),
  })
  .refine(
    (data) => {
      for (const action of data.actions) {
        if (
          action.actionType === ActionType.VIEWING_POLICY_LIST &&
          (!action.metadata?.hrefs || action.metadata.hrefs.length === 0)
        ) {
          return false;
        }
      }

      return true;
    },
    {
      error: 'Viewing Policy List actions require at least one policy',
      path: ['actions'],
    },
  );

export type RuleFormType = z.infer<typeof formSchema>;

export function RuleForm({ defaultFormValues, loading, onReset, onSubmit }: RuleFormProps) {
  const { esni } = useAppSelector(selectToolsConfig);
  const { checkGroupAccess } = useAccessCheck();

  const [editMode, setEditMode] = useState<boolean>(!defaultFormValues);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const form = useForm<RuleFormType>({
    defaultValues: {
      actions: [],
      name: '',
      priority: 1,
      query: '',
      status: EsniRuleStatus.TESTING,
      ...defaultFormValues,
    },
    disabled: loading || !editMode,
    resolver: zodResolver(formSchema),
  });

  const getFormFooter = () => {
    if (checkGroupAccess(esni.auth.pages.rules.featureGroups.createRule) && !defaultFormValues) {
      return (
        <>
          <Button key="form-cancel-button" disabled={loading} type="reset" variant="secondary">
            Cancel
          </Button>
          <Button
            key="form-submit-button"
            disabled={form.formState.disabled}
            loading={loading}
            type="submit">
            Create
          </Button>
        </>
      );
    } else if (
      checkGroupAccess(esni.auth.pages.rules.featureGroups.editRule) &&
      defaultFormValues
    ) {
      if (editMode) {
        return (
          <>
            <Button
              key="edit-mode-cancel-button"
              disabled={loading}
              type="button"
              variant="secondary"
              onClick={() => {
                setEditMode(false);
                form.reset();
              }}>
              Cancel
            </Button>
            <Button
              key="submit-button"
              disabled={form.formState.disabled}
              loading={loading}
              type="submit">
              Update
            </Button>
          </>
        );
      } else {
        return (
          <>
            {checkGroupAccess(esni.auth.pages.rules.featureGroups.deleteRule) && (
              <Button
                key="delete-button"
                type="button"
                variant="destructive"
                onClick={() => {
                  setDeleteDialogOpen(true);
                }}>
                Delete Rule
              </Button>
            )}
            <Button
              key="edit-button"
              type="button"
              onClick={() => {
                setEditMode(true);
              }}>
              Edit Rule
            </Button>
          </>
        );
      }
    } else if (
      checkGroupAccess(esni.auth.pages.rules.featureGroups.deleteRule) &&
      defaultFormValues
    ) {
      return (
        <Button
          key="delete-button"
          type="button"
          variant="destructive"
          onClick={() => {
            setDeleteDialogOpen(true);
          }}>
          Delete Rule
        </Button>
      );
    }
  };

  const handleOnDelete = () => {
    onReset();
  };

  return (
    <>
      <Form {...form}>
        <form
          className="flex flex-col gap-4"
          onReset={onReset}
          onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <div className="flex flex-wrap items-start gap-4">
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select
                      defaultValue={field.value}
                      disabled={field.disabled}
                      onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="Rule Type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.values(EsniRuleType).map((type) => (
                          <SelectItem key={type} value={type}>
                            {formatEnumValue(type)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select
                      defaultValue={field.value}
                      disabled={field.disabled}
                      onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="Rule Status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.values(EsniRuleStatus).map((type) => (
                          <SelectItem key={type} value={type}>
                            {formatEnumValue(type)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} className="w-20" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>
          <FormField
            control={form.control}
            name="query"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>Query</FormLabel>
                  <FormControl>
                    <Textarea {...field} className="h-32 font-mono" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="actions"
            render={({ field }) => {
              return (
                <FormItem>
                  <ActionTypeMultiSelect
                    disabled={field.disabled}
                    modal={true}
                    selectedActions={field.value}
                    onActionSelectionChanged={field.onChange}
                  />
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
            {getFormFooter()}
          </div>
        </form>
      </Form>
      <DeleteEsniRuleDialog
        name={defaultFormValues?.name ?? ''}
        open={deleteDialogOpen}
        ruleId={defaultFormValues?.id ?? ''}
        onDeleteEnsiRule={handleOnDelete}
        onOpenChange={setDeleteDialogOpen}
      />
    </>
  );
}
